import { PERUT_LOGO } from '../../config.js';
import { initials, pct } from '../../lib/format.js';
import { Icon } from './Icon.jsx';
import { useSola } from '../../sola.jsx';
import { t } from '../../i18n/index.js';

/* Majhni gradniki, ki jih uporablja cel app. */

/** Logotip trenutne šole; brez logotipa pokaže ime šole v znaku. */
export function Logo({ height = 40, className = '', sola: override }) {
  const ctx = useSola();
  const sola = override ?? ctx.sola;
  const src = sola?.logo_url || (sola?.id ? null : PERUT_LOGO);
  if (!src) {
    return (
      <span
        className={`logo logo--text ${className}`.trim()}
        style={{ height, fontSize: Math.max(13, Math.round(height * 0.42)) }}
        aria-label={sola?.ime ?? 'Učilnica'}
      >
        {(sola?.kratko || sola?.ime || 'Učilnica').slice(0, 24)}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={sola?.ime ?? 'Perut Paragliding'}
      className={`logo ${className}`.trim()}
      style={{ height, width: 'auto', objectFit: 'contain' }}
    />
  );
}

export function Avatar({ ime, priimek, size = 40 }) {
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-hidden="true"
    >
      {initials(ime, priimek)}
    </span>
  );
}

/** Vodoravna črta napredka. Vedno ima besedilni ekvivalent za bralnik. */
export function Bar({ done, total, thick = false, ok = false, label }) {
  const p = pct(done, total);
  return (
    <div
      className={`bar${thick ? ' bar--thick' : ''}`}
      role="progressbar"
      aria-valuenow={p}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? t('{a} od {b}', { a: done, b: total })}
    >
      <div className={`bar__fill${ok ? ' bar__fill--ok' : ''}`} style={{ width: `${p}%` }} />
    </div>
  );
}

/**
 * Krožni prikaz odstotka. Uporabljen za glavno številko napredka —
 * krog nosi tako številko kot obliko, kar je na majhnem zaslonu bolj
 * berljivo od vrstice s tremi podatki.
 */
export function Ring({ done, total, size = 76, stroke = 7, caption }) {
  const p = pct(done, total);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const full = total > 0 && done >= total;

  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle
          className="ring__track"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className={`ring__fill${full ? ' ring__fill--ok' : ''}`}
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={c - (c * p) / 100}
        />
      </svg>
      <span className="ring__label">
        <strong
          className="num"
          style={{ fontSize: size * 0.26, color: full ? 'var(--ok-text)' : 'var(--brand-ink)' }}
        >
          {p}%
        </strong>
        {caption && (
          <span className="t-xs t-dimmer" style={{ marginTop: 2 }}>
            {caption}
          </span>
        )}
      </span>
      <span className="sr-only">
        {t('{a} od {b} — {p} odstotkov', { a: done, b: total, p })}
      </span>
    </div>
  );
}

/**
 * Značka stanja vaje ali leta.
 *
 * Brez stanja ne pokaže ničesar. Prejšnja verzija je „⏳ Čaka potrditev"
 * izpisala tudi ob vsaki nedotaknjeni pod-vaji, zato je bil cel program
 * videti kot stena čakajočih opravil.
 */
export function StatusBadge({ status }) {
  if (status === 'confirmed')
    return (
      <span className="badge badge--ok">
        <Icon name="check" size={13} /> {t('Potrjeno')}
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="badge badge--bad">
        <Icon name="x" size={13} /> {t('Zavrnjeno')}
      </span>
    );
  if (status === 'pending')
    return (
      <span className="badge badge--wait">
        <Icon name="clock" size={13} /> {t('Čaka')}
      </span>
    );
  return null;
}

export function Empty({ icon = 'info', title, hint, children }) {
  return (
    <div className="empty">
      <div className="empty__icon">
        <Icon name={icon} size={34} />
      </div>
      {title && <div className="empty__title">{title}</div>}
      {hint && <div className="empty__hint">{hint}</div>}
      {children}
    </div>
  );
}

export function Skeleton({ w = '100%', h = 16, style }) {
  return <div className="skeleton" style={{ width: w, height: h, ...style }} aria-hidden="true" />;
}

/** Nalaganje seznama — pokaže obliko tega, kar prihaja. */
export function SkeletonList({ rows = 5 }) {
  return (
    <div className="list" aria-busy="true" aria-label={t('Nalagam')}>
      {Array.from({ length: rows }, (_, i) => (
        <div className="list__row" key={i}>
          <Skeleton w={32} h={32} style={{ borderRadius: '50%' }} />
          <div className="spacer stack stack--tight">
            <Skeleton w="45%" h={14} />
            <Skeleton w="70%" h={10} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Polje z oznako, namigom in napako — da so obrazci povsod enaki. */
export function Field({ label, hint, error, required, children, id }) {
  return (
    <div className="field">
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
          {required && (
            <span className="t-bad" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="field__hint">{hint}</span>
      )}
    </div>
  );
}

/** Iskalno polje z ikono in gumbom za počisti. */
export function Search({ value, onChange, placeholder = t('Išči'), label }) {
  return (
    <div className="search spacer">
      <span className="search__icon">
        <Icon name="search" size={17} />
      </span>
      <input
        className="input"
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label ?? placeholder}
      />
      {value && (
        <button
          className="btn-icon btn-icon--sm search__clear"
          onClick={() => onChange('')}
          aria-label={t('Počisti iskanje')}
        >
          <Icon name="x" size={14} />
        </button>
      )}
    </div>
  );
}
